import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommonService } from '../../shared/services/common.service';

@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly commonService: CommonService
  ) {}

  /**
   * Validates input against suspicious patterns for XSS and injection attacks
   */
  validateInput(input: string): { isValid: boolean; issue?: string } {
    if (!input) return { isValid: true };

    const normalizedInput = input.toLowerCase().replace(/\s+/g, ' ').trim();

    const suspiciousPatterns: { pattern: RegExp; message: string }[] = [
      // ---- XSS: Script tags (bounded, no dot-star) ----
      {
        pattern: /<script\b[^>]{0,200}>/i,
        message: 'Script tag opening detected',
      },
      {
        pattern: /<\/script>/i,
        message: 'Script tag closing detected',
      },

      // ---- Dangerous URI schemes ----
      {
        pattern: /\bjavascript\s*:/i,
        message: 'JavaScript URI detected',
      },
      {
        pattern: /\bvbscript\s*:/i,
        message: 'VBScript URI detected',
      },
      {
        pattern: /\bdata\s*:[^,]{0,100},/i,
        message: 'Data URI scheme detected',
      },

      // ---- Event handlers (explicit list, no grouping repetition) ----
      {
        pattern:
          /\bon(click|change|focus|input|keydown|keyup|mousedown|mouseup|scroll|submit|error)\s*=/i,
        message: 'Event handler attribute detected',
      },

      // ---- Dangerous HTML tags ----
      {
        pattern:
          /<(iframe|embed|object|applet|form|input|textarea|select|button|meta|link|style|base|frameset|frame)\b/i,
        message: 'Potentially dangerous HTML tag detected',
      },

      // ---- NoSQL injection ----
      {
        pattern: /\$(where|ne|gt|lt|gte|lte|in|nin|exists|regex)\b/i,
        message: 'NoSQL injection operator detected',
      },

      // ---- Server-side template injection ----
      {
        pattern: /(\{\{)|(\}\})|({%|%})|(\{#|#\})/,
        message: 'Template injection pattern detected',
      },

      // ---- XML injection ----
      {
        pattern: /<!\[cdata\[|<\?xml|<!doctype|<!entity/i,
        message: 'XML injection pattern detected',
      },

      // ---- Expression Language injection ----
      {
        pattern: /(\$\{)|(#\{)/,
        message: 'Expression Language injection detected',
      },

      // ---- Encoding bypass ----
      {
        pattern: /(\\u[0-9a-f]{4})|(\\x[0-9a-f]{2})|(&#x?[0-9a-f]{1,6};)/i,
        message: 'Encoded characters detected',
      },
    ];

    for (const { pattern, message } of suspiciousPatterns) {
      if (pattern.test(input) || pattern.test(normalizedInput)) {
        return { isValid: false, issue: message };
      }
    }

    // ---- Suspicious JS keyword clustering (no regex) ----
    const suspiciousKeywords = [
      'settimeout',
      'setinterval',
      'constructor',
      'prototype',
      'innerhtml',
      'outerhtml',
      'document.write',
      'window.location',
    ];

    const keywordHits = suspiciousKeywords.filter(k =>
      normalizedInput.includes(k)
    ).length;

    if (keywordHits >= 1) {
      return {
        isValid: false,
        issue: 'Suspicious JavaScript keyword detected',
      };
    }

    // ---- Special character ratio (linear scan) ----
    let specialCount = 0;
    for (const c of input) {
      if ('<>\'"&%$#@!(){}[]\\|`~'.includes(c)) {
        specialCount++;
      }
    }

    if (specialCount / input.length > 0.3) {
      return {
        isValid: false,
        issue: 'Excessive special characters detected',
      };
    }

    return { isValid: true };
  }

  showSecurityAlert(message: string, fieldName: string): void {
    const alertMessage = `Security Warning: ${message} in field "${fieldName}". This data will not be sent.`;
    console.error(alertMessage);
    this.commonService.notify('error', alertMessage, 'Security Alert', '500px');
  }

  sanitizeHtml(content: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(content); // NOSONAR: Bypass is safe: content is generated internally, not from user input
  }

  encodeHtml(value: string): string {
    if (!value) return '';
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  getSecurityHeaders(): Record<string, string> {
    return {
      'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
      'Content-Security-Policy':
        "default-src 'self'; script-src 'self'; img-src 'self' data:; style-src 'self' 'unsafe-inline'; font-src 'self'; connect-src 'self'",
      'X-Frame-Options': 'SAMEORIGIN',
      'X-Content-Type-Options': 'nosniff',
      'X-XSS-Protection': '1; mode=block',
      'Referrer-Policy': 'strict-origin-when-cross-origin',
    };
  }
}
